import React, { useRef, useState } from 'react';
import { ActionIcon, Alert, Badge, Group, Progress, Text } from '@mantine/core';
import { IconCloudUpload, IconFile, IconInfoCircle, IconTrash, IconUpload } from '@tabler/icons-react';
import { formatBytes, totalSize } from './service/fileShareApi';

export default function FileUploader({ files, onChange, maxFileSizeMb, maxShareSizeMb, disabled, uploadProgress = null }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const addFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    if (!nextFiles.length) return;
    const tooBig = nextFiles.find((file) => file.size > maxFileSizeMb * 1024 ** 2);
    if (tooBig) return setError(`${tooBig.name} dosyası ${maxFileSizeMb} MB sınırını aşıyor.`);
    const merged = [...files];
    for (const file of nextFiles) {
      if (!merged.some((f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified)) merged.push(file);
    }
    if (totalSize(merged) > maxShareSizeMb * 1024 ** 2) return setError(`Toplam paylaşım boyutu ${maxShareSizeMb} MB sınırını aşıyor.`);
    setError(''); onChange(merged);
    if (inputRef.current) inputRef.current.value = '';
  };
  return <div>
    <input ref={inputRef} type="file" multiple hidden onChange={(event) => addFiles(event.target.files)} disabled={disabled} />
    <div role="button" tabIndex={0} className={`fs-dropzone${dragging ? ' dragging' : ''}`} aria-label="Dosya seçmek için tıklayın"
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => { if ((event.key === 'Enter' || event.key === ' ') && !disabled) { event.preventDefault(); inputRef.current?.click(); } }}
      onDragOver={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
      onDrop={(event) => { event.preventDefault(); setDragging(false); if (!disabled) addFiles(event.dataTransfer.files); }}
    >
      <div className="fs-dropzone-icon"><IconCloudUpload size={31} stroke={1.7}/></div>
      <Text fw={750} size="md" mt="md">Dosyalarınızı buraya sürükleyin</Text>
      <Text size="sm" c="dimmed" mt={4}>veya bilgisayarınızdan seçmek için tıklayın</Text>
      <span className="fs-pick-button"><IconUpload size={16}/> Dosya seç</span>
      <Text size="xs" c="dimmed" mt="md">Tek dosya: {formatBytes(maxFileSizeMb * 1024 ** 2)} · Toplam: {formatBytes(maxShareSizeMb * 1024 ** 2)}</Text>
    </div>
    {error && <Alert color="red" variant="light" mt="sm" icon={<IconInfoCircle size={16}/>} withCloseButton onClose={() => setError('')}>{error}</Alert>}
    {files.length > 0 && <div className="fs-file-list">
      <Group justify="space-between" mb="sm"><Group gap="xs"><Text fw={700} size="sm">Seçilen dosyalar</Text><Badge color="indigo" variant="light" size="sm">{files.length}</Badge></Group><Text size="xs" c="dimmed">Toplam {formatBytes(totalSize(files))}</Text></Group>
      {files.map((file, index) => <div className="fs-upload-file" key={`${file.name}-${file.size}-${index}`}>
        <div className="fs-file-icon"><IconFile size={19}/></div>
        <div className="fs-file-name"><Text size="sm" fw={650} truncate="end">{file.name}</Text><Text size="xs" c="dimmed">{formatBytes(file.size)} · Yüklemeye hazır</Text></div>
        <ActionIcon variant="subtle" color="gray" aria-label="Dosyayı kaldır" disabled={disabled} onClick={() => onChange(files.filter((_, i) => i !== index))}><IconTrash size={17}/></ActionIcon>
      </div>)}
      
      {uploadProgress !== null && <Progress mt="md" color="indigo" value={uploadProgress} size={6} radius="xl"/>}
      <Text size="xs" c="dimmed" mt={8}>{uploadProgress !== null
        ? `Yükleme: %${uploadProgress} · Sunucunun kaydetmesi tamamlanana kadar bekleyin.`
        : "Dosyalar 'Paylaşım Linki Oluştur' dediğinizde sunucuya yüklenir."}</Text>
    </div>}
  </div>;
}
